// The colour wheel from the RGB / WWRGB screens.
//
// Flutter uses flutter_colorpicker with paletteType hsvWithHue and
// pickerAreaHeightPercent 0.5 — a saturation/value square with a hue strip under
// it. Rather than pull in a picker library for one screen, the same two controls
// are built here from CSS gradients: white→hue horizontally over
// transparent→black vertically is exactly an HSV plane.
//
// Dragging reports continuously; the caller debounces before publishing (the
// Flutter screen waits 300 ms) so a drag doesn't flood the device with frames.

import React, { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { hsvToRgb, rgbToHsv } from '../../../../../core/devices/light';

const HUE_STRIP = 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%,'
  + ' #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)';

export default function ColorArea({ rgb, onChange, width }) {
  const areaRef = useRef(null);
  const [h, s, v] = rgbToHsv(rgb[0], rgb[1], rgb[2]);
  const areaHeight = width * 0.5; // pickerAreaHeightPercent: .5

  const emit = useCallback((hh, ss, vv) => {
    onChange(hsvToRgb(hh, ss, vv));
  }, [onChange]);

  const handleArea = useCallback((clientX, clientY) => {
    const el = areaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (clientY - r.top) / r.height));
    emit(h, x, 1 - y);
  }, [emit, h]);

  const handleHue = useCallback((clientX, el) => {
    const r = el.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    emit(x * 360, s || 1, v || 1);
  }, [emit, s, v]);

  return (
    <Box sx={{ width, maxWidth: '100%' }}>
      <Box
        ref={areaRef}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleArea(e.clientX, e.clientY); }}
        onPointerMove={(e) => { if (e.buttons) handleArea(e.clientX, e.clientY); }}
        sx={{
          position: 'relative',
          width: '100%',
          height: areaHeight,
          borderRadius: '8px',
          touchAction: 'none',
          cursor: 'crosshair',
          background: `linear-gradient(to top, #000, transparent),
                       linear-gradient(to right, #fff, hsl(${h}, 100%, 50%))`,
        }}
      >
        <Box sx={{
          position: 'absolute',
          left: `${s * 100}%`,
          top: `${(1 - v) * 100}%`,
          width: 18,
          height: 18,
          ml: '-9px',
          mt: '-9px',
          borderRadius: '50%',
          border: '2px solid #fff',
          boxShadow: '0 0 3px rgba(0,0,0,0.6)',
          bgcolor: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`,
          pointerEvents: 'none',
        }}
        />
      </Box>

      <Box
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleHue(e.clientX, e.currentTarget); }}
        onPointerMove={(e) => { if (e.buttons) handleHue(e.clientX, e.currentTarget); }}
        sx={{
          position: 'relative',
          width: '100%',
          height: 26,
          mt: 1.5,
          borderRadius: '13px',
          background: HUE_STRIP,
          touchAction: 'none',
          cursor: 'pointer',
        }}
      >
        <Box sx={{
          position: 'absolute',
          left: `${(h / 360) * 100}%`,
          top: '50%',
          width: 22,
          height: 22,
          ml: '-11px',
          mt: '-11px',
          borderRadius: '50%',
          border: '2px solid #fff',
          boxShadow: '0 0 3px rgba(0,0,0,0.6)',
          bgcolor: `hsl(${h}, 100%, 50%)`,
          pointerEvents: 'none',
        }}
        />
      </Box>
    </Box>
  );
}
